import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Icon, Logo } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import {
    trackGA4AndNavigate,
    trackGA4UserLoginSuccess,
    trackGA4UserRegistrationSuccess,
} from '@gumtree/shared/src/util/ga4-shared';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';

import * as searchBarAct from '../../search-bar/search-bar-actions';
import SearchBar from '../../search-bar/search-bar-container';
import SideMenu from '../../side-menu/side-menu';
import LoginModal from '../../../login-modal/app';
import { StyledDialog } from '../../../login-modal/app.style';
import { useModal } from '../../../use-modal';
import {
    LoginModalType,
    setIsOpen,
    showForgotPasswordModal,
} from '../../../reducers/common/login-modal';
import type { ShellState, ShellUseDispatch } from '../../../reducers/common';


import { useSearchHotKeywords } from './use-search-hot-keywords';
import { useSearchTriggerKeyword } from './use-search-trigger-keyword';
import {
    MhhContainer,
    MhhInner,
    LeftSection,
    LogoWrapper,
    SearchTrigger,
    PlaceholderScrollWrapper,
    PlaceholderText,
    PlaceholderTextStatic,
    ActionsWrapper,
    iconButtonCss,
    IconLabel,
    HiddenSearchBarWrapper,
} from './mhh-nav-bar.style';
import PostAdAction from '../post-ad-action';

export default function MhhNavBar() {
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const { openDialog, closeDialog } = useModal();
    const dispatch = useDispatch() as ShellUseDispatch;
    const { baseConfig, userData, isOpen, lastSearchKeyword, locationId } = useSelector(
        ({ baseConfig, userData, loginModal: { isOpen }, searchBar }: ShellState) => ({
            baseConfig,
            userData,
            isOpen,
            lastSearchKeyword: searchBar?.keyword?.value || '',
            locationId: searchBar?.location?.id,
        }),
        shallowEqual
    );
    const hotKeyword = useSearchHotKeywords(locationId);
    const { displayKeyword, isShowingHotKeyword, recordEntryHotKeyword } =
        useSearchTriggerKeyword(hotKeyword, lastSearchKeyword);

    const loggedIn = userData && userData.userLoggedIn;
    const pageType = baseConfig?.pageType;

    const openSearchModalNonBlocking = useCallback(() => {
        recordEntryHotKeyword();
        if (isShowingHotKeyword && hotKeyword) {
            dispatch(
                searchBarAct.setKeywordAndUpdateSuggestions(hotKeyword, { populatedBy: 'popular' })
            );
        }
        window.requestAnimationFrame(() => {
            window.setTimeout(() => {
                dispatch(searchBarAct.toggleSearchModal());
            }, 0);
        });
    }, [dispatch, hotKeyword, lastSearchKeyword, recordEntryHotKeyword]);

    React.useEffect(() => {
        trackGA4UserLoginSuccess();
        trackGA4UserRegistrationSuccess();

        const params = new URLSearchParams(window.location.search);
        const resetPassword = params.get('reset-password');
        const isRegistrationSuccessful = params.get('new-registration');

        if (isRegistrationSuccessful) {
            setRegistrationSuccess(true);
            dispatch(setIsOpen(true));
            params.delete('new-registration');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        } else if (resetPassword) {
            dispatch(setIsOpen(true));
            dispatch(showForgotPasswordModal({ value: true, isReset: true }));
            params.delete('reset-password');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    const openSearchBar = () => {
        closeDialog();
    };

    return (
        <MhhContainer data-q="mhh-nav-bar" isSticky={pageType === 'Homepage'}>
            <MhhInner>
                <LeftSection>
                    <LogoWrapper>
                        <Logo
                            linkTo="/"
                            withText={false}
                            small
                            {...qaAttribute('gumtree-logo')}
                            data-testid="gumtree-logo"
                            onClick={(e: React.MouseEvent) =>
                                trackGA4AndNavigate<GA4.ClickPrimaryLogo>({
                                    href: '/',
                                    mouseEvent: e,
                                    payload: {
                                        event: 'click_primary_logo',
                                        linkDomain: getLinkHostname('/'),
                                        linkText: 'Gumtree',
                                        linkURL: '/',
                                    },
                                })
                            }
                        />
                    </LogoWrapper>

                    <SearchTrigger
                        onClick={openSearchModalNonBlocking}
                        aria-label="Search Gumtree.com"
                        {...qaAttribute('mhh-search-trigger')}
                        data-testid="mhh-search-trigger"
                    >
                        <PlaceholderScrollWrapper>
                            {displayKeyword ? (
                                isShowingHotKeyword ? (
                                    <PlaceholderText key={displayKeyword}>
                                        {displayKeyword}
                                    </PlaceholderText>
                                ) : (
                                    <PlaceholderTextStatic>
                                        {displayKeyword}
                                    </PlaceholderTextStatic>
                                )
                            ) : (
                                <PlaceholderTextStatic>
                                    Search Gumtree
                                </PlaceholderTextStatic>
                            )}
                        </PlaceholderScrollWrapper>
                    </SearchTrigger>
                </LeftSection>

                <ActionsWrapper>
                    <PostAdAction
                        useMhhLayout
                        useNewIcons
                        loggedIn={!!loggedIn}
                        isMobile
                        pageType={pageType}
                        categoryL2={baseConfig.category?.l2Category}
                        buyerUrl={baseConfig.buyerUrl}
                        openDialog={openDialog}
                        sellModalType={LoginModalType.SELL}
                    />

                    {!loggedIn && (
                        <button
                            type="button"
                            css={iconButtonCss}
                            {...qaAttribute('hm-registration')}
                            data-testid="mhh-signup-button"
                            onClick={(e) => {
                                openDialog(e as any, LoginModalType.REGISTER, false);
                            }}
                        >
                            <Icon type="navbar-signup" />
                            <IconLabel>Sign up</IconLabel>
                        </button>
                    )}

                    <SideMenu
                        baseConfig={baseConfig}
                        isLoggedIn={!!userData}
                        openDialog={openDialog}
                        isMhhExperiment
                        useMhhLayout
                    />
                </ActionsWrapper>
            </MhhInner>

            <HiddenSearchBarWrapper>
                <SearchBar />
            </HiddenSearchBarWrapper>

            <StyledDialog isOpen={isOpen} onClose={closeDialog}>
                <LoginModal
                    registrationSuccess={registrationSuccess}
                    openSearchBar={openSearchBar}
                />
            </StyledDialog>
        </MhhContainer>
    );
}
